// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {connectionToNodes} from '@wandb/weave/core/ops/domain/util';
import {useRefreshAllNodes} from '@wandb/weave/react';
import React, {useCallback, useEffect, useState} from 'react';

import {useEntityTagsQuery} from '../../../../generated/graphql';
import {CollectionInfo} from '../../../ModelRegistry/utils';
import {mapGQLTags} from '../../../TagAssignment';
import {REGISTRY_PROJECT_PREFIX} from '../../common/utils';
import * as S from './CreateRegisteredCollectionDrawerInner.styles';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {DrawerPageAddVersion} from './DrawerPageAddVersion';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {DrawerPageCollectionConfiguration} from './DrawerPageCollectionConfiguration';
import {
  useAddTagsToCollection,
  useCreateRegisteredCollection,
  useDefaultProjectExists,
  useLinkArtifactVersion,
} from './hooks';
import {ArtifactVersion} from './SelectArtifactCollectionVersion';

export type Collection = {
  name: string;
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  description: string;
  tags: string[];
  numVersions: number; // number of artifacts linked to the collection
  id?: string;
  aliases?: string[];
};

export type Artifact = {
  entityName: string;
  projectName: string;
  sequenceName: string;
  alias: string; // an alias string or version index that identifies the artifact
  id: string;
  versionIndex?: number;
  ttlDurationSeconds?: number;
  createdAt?: string;
};

type CreateRegisteredCollectionDrawerInnerProps = {
  onClose: () => void;
  registryEntityName?: string;
  artifactTypeName?: string;
  defaultProjectName: string;
  existingCollections?: CollectionInfo[];
  registryAllowsAllArtifactTypes?: boolean;
  refetchCollections?: () => void;
};

enum Steps {
  CONFIGURE_COLLECTION,
  ADD_VERSION,
}

export const CreateRegisteredCollectionDrawerInner = ({
  registryEntityName,
  artifactTypeName,
  defaultProjectName,
  onClose,
  existingCollections,
  registryAllowsAllArtifactTypes,
  refetchCollections,
}: CreateRegisteredCollectionDrawerInnerProps) => {
  // general hooks
  const refreshNodes = useRefreshAllNodes();

  // Common state variables
  const [step, setStep] = useState(Steps.CONFIGURE_COLLECTION);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);

  // Collection-level variables
  const [collection, setCollection] = useState<Collection>({
    name: '',
    entityName: registryEntityName ?? '',
    projectName: defaultProjectName,
    artifactTypeName: '',
    tags: [],
    description: '',
    numVersions: 0,
  });

  const [isInvalidCollectionName, setIsInvalidCollectionName] = useState(false);
  const [isInvalidArtifactTypeName, setIsInvalidArtifactTypeName] =
    useState(false);

  // Artifact version - level variables
  const isOrgRegistry = collection.projectName.startsWith(
    REGISTRY_PROJECT_PREFIX
  );
  const [linkedArtifact, setLinkedArtifact] = useState<Artifact>({
    entityName: isOrgRegistry ? '' : collection.entityName,
    projectName: '',
    sequenceName: '',
    alias: '',
    id: '',
  });

  // Variables needed to create the collection
  const defaultProjectDoesNotExist = useDefaultProjectExists(
    defaultProjectName,
    collection.entityName ?? ''
  );
  const {
    createRegisteredCollection,
    loading: loadingCreateRegisteredCollection,
  } = useCreateRegisteredCollection(
    collection.entityName,
    collection.projectName
  );

  // Tags
  const {data: entityData} = useEntityTagsQuery({
    variables: {
      entityName: collection.entityName ?? '',
    },
    skip: !collection.entityName || collection.entityName === '',
  });
  const entity = entityData?.entity;
  const allEntityTags = React.useMemo(() => {
    const rawTags =
      entity?.tagCategories?.flatMap(tc => connectionToNodes(tc.tags)) ?? [];
    const listRawTags = mapGQLTags(rawTags);
    return listRawTags.map(rawTag => rawTag.name);
  }, [entity]);
  const {createTagAssignment, loading: loadingCreateTagAssignment} =
    useAddTagsToCollection();

  const [artifactVersions, setArtifactVersions] = useState<ArtifactVersion[]>(
    []
  );
  const {linkArtifactVersion, loading: loadingLinkVersion} =
    useLinkArtifactVersion();

  const onSelectEntity = useCallback(
    (selectedEntity: string) => {
      setCollection({...collection, entityName: selectedEntity});
      setLinkedArtifact({
        entityName: selectedEntity,
        projectName: '',
        sequenceName: '',
        alias: '',
        id: '',
        versionIndex: 0,
      });
    },
    [collection, setCollection, setLinkedArtifact]
  );

  // Enable or disable button to go to the next page based on user input
  useEffect(() => {
    const existingCollectionName = existingCollections
      ?.filter(
        c =>
          c.entityName === collection.entityName &&
          c.projectName === collection.projectName
      )
      .some(c => c?.collectionName === collection.name);
    if (
      isInvalidCollectionName ||
      collection.name === '' ||
      collection.name.length < 3 ||
      !collection.entityName ||
      defaultProjectDoesNotExist.loading ||
      isInvalidArtifactTypeName ||
      collection.artifactTypeName === ''
    ) {
      setNextButtonDisabled(true);
    } else if (existingCollectionName) {
      setNextButtonDisabled(true);
    } else {
      setNextButtonDisabled(false);
    }
  }, [
    step,
    collection,
    registryEntityName,
    defaultProjectName,
    existingCollections,
    defaultProjectDoesNotExist.loading,
    isInvalidCollectionName,
    setNextButtonDisabled,
    onSelectEntity,
    linkedArtifact,
    isInvalidArtifactTypeName,
  ]);

  return (
    <S.RegisteredCollectionDrawer>
      <S.CenteredFlex>
        <S.Header>Create collection</S.Header>
        <S.CloseButton>
          <Button onClick={onClose} icon="close" size="large" variant="ghost" />
        </S.CloseButton>
      </S.CenteredFlex>
      <S.Divider />
      <S.RegisteredCollectionDrawerMain>
        {step === Steps.CONFIGURE_COLLECTION && (
          <DrawerPageCollectionConfiguration
            collection={collection}
            entityTagsList={allEntityTags}
            showModelLimitReached={false}
            isInvalidCollectionName={isInvalidCollectionName}
            existingCollections={existingCollections ?? []}
            setCollection={setCollection}
            setIsInvalidCollectionName={setIsInvalidCollectionName}
            onSelectEntity={onSelectEntity}
            defaultArtifactTypeName={artifactTypeName}
            isInvalidArtifactTypeName={isInvalidArtifactTypeName}
            setIsInvalidArtifactTypeName={setIsInvalidArtifactTypeName}
            registryAllowsAllArtifactTypes={registryAllowsAllArtifactTypes}
          />
        )}
        {step === Steps.ADD_VERSION && (
          <DrawerPageAddVersion
            collection={collection}
            defaultProjectName={defaultProjectName}
            artifact={linkedArtifact}
            artifactVersions={artifactVersions}
            setArtifact={setLinkedArtifact}
            setArtifactVersions={setArtifactVersions}
          />
        )}
      </S.RegisteredCollectionDrawerMain>
      <S.Footer>
        {step === Steps.CONFIGURE_COLLECTION ? (
          <S.FooterLeftContent>
            <S.HelpMessage>
              You may link a version in the next step.
            </S.HelpMessage>
          </S.FooterLeftContent>
        ) : (
          <S.FooterLeftContent>
            <Button
              className="create-button"
              variant="secondary"
              size="large"
              icon="back"
              onClick={() => {
                setStep(step - 1);
              }}>
              Back
            </Button>
          </S.FooterLeftContent>
        )}
        <S.FooterRightContent>
          <Button
            className="create-button"
            variant="primary"
            size="large"
            onClick={async () => {
              const createCollectionResult = await createRegisteredCollection(
                collection.name.trim(),
                collection.description,
                collection.entityName ?? '',
                defaultProjectName,
                collection.artifactTypeName,
                defaultProjectDoesNotExist.data
              );
              if (createCollectionResult && collection.tags.length > 0) {
                await createTagAssignment(
                  collection.tags,
                  collection.entityName ?? '',
                  defaultProjectName,
                  collection.name
                );
              }
              if (step === Steps.ADD_VERSION && linkedArtifact.id) {
                await linkArtifactVersion(
                  linkedArtifact.id,
                  createCollectionResult?.id ?? '',
                  collection.name,
                  []
                );
              }
              await refreshNodes();
              await refetchCollections?.();
              onClose();
            }}
            disabled={
              nextButtonDisabled ||
              loadingCreateRegisteredCollection ||
              loadingCreateTagAssignment ||
              loadingLinkVersion
            }>
            Create collection
          </Button>
          {step !== Steps.ADD_VERSION && (
            <Button
              variant="secondary"
              size="large"
              endIcon="forward-next"
              onClick={() => {
                setStep(step + 1);
              }}
              disabled={
                nextButtonDisabled ||
                loadingCreateRegisteredCollection ||
                loadingCreateTagAssignment
              }>
              Link version
            </Button>
          )}
        </S.FooterRightContent>
      </S.Footer>
    </S.RegisteredCollectionDrawer>
  );
};
